import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CodeIcon from '@mui/icons-material/Code';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InventoryIcon from '@mui/icons-material/Inventory';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

type StrategyStatus = 'DRAFT' | 'TESTING' | 'LIVE';

interface Strategy {
  id: string;
  name: string;
  description: string;
  status: StrategyStatus;
  createdAt: Date;
  updatedAt: Date;
  isPublic: boolean;
  lastBacktest?: {
    symbol: string;
    startDate: string;
    endDate: string;
    timeframe: string;
    initialCapital: number;
  };
  lastBacktestResult?: {
    totalReturn: number;
    maxDrawdown: number;
    sharpeRatio: number;
  };
  deploymentData?: any;
  metadata?: {
    deployment?: {
      status?: string;
      exchange?: string;
    };
    exchange?: string;
    [key: string]: any;
  };
}

export const StrategiesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAuth();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);

  useEffect(() => {
    if (state.user) {
      loadStrategies();
    }
  }, [state.user]);

  const loadStrategies = async () => {
    if (!state.user?.id) return;
    
    try {
      setLoading(true);
      
      const strategiesRef = collection(db, `users/${state.user.id}/strategies`);
      
      // Only get LIVE strategies that have deployment data
      const q = query(strategiesRef, where('status', '==', 'LIVE'), orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const strategies: Strategy[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        
        // Only include strategies with deployment metadata
        if (data.deploymentData || data.deploymentStatus || 
            (data.metadata && (data.metadata.deployment || data.metadata.exchange))) {
          strategies.push({
            id: doc.id,
            name: data.name || 'Untitled Strategy',
            description: data.description || '',
            status: data.status || 'DRAFT',
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            isPublic: data.isPublic || false,
            lastBacktest: data.lastBacktest,
            lastBacktestResult: data.lastBacktestResult,
            deploymentData: data.deploymentData,
            metadata: data.metadata
          });
        }
      });
      
      setStrategies(strategies);
    } catch (error) {
      console.error('Error loading strategies:', error);
      setError('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  // No strategy creation on Live Strategies page

  const handleEditStrategy = (id: string) => {
    navigate(`/strategy/${id}`);
  };

  const handleDeleteStrategy = async () => {
    if (!state.user?.id || !strategyToDelete) return;
    
    try {
      await deleteDoc(doc(db, `users/${state.user.id}/strategies/${strategyToDelete}`));
      setStrategies((prev) => prev.filter((s) => s.id !== strategyToDelete));
      setDeleteConfirmOpen(false);
      setStrategyToDelete(null);
    } catch (error) {
      console.error('Error deleting strategy:', error);
      setError('Failed to delete strategy');
    }
  };

  const confirmDelete = (id: string) => {
    setStrategyToDelete(id);
    setDeleteConfirmOpen(true);
    handleCloseMenu();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, strategyId: string) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedStrategyId(strategyId);
  };

  const handleCloseMenu = () => {
    setActionMenuAnchor(null);
    setSelectedStrategyId(null);
  };

  const handleTogglePublic = async (strategy: Strategy) => {
    if (!state.user?.id) return;
    
    try {
      const newValue = !strategy.isPublic;
      await updateDoc(doc(db, `users/${state.user.id}/strategies/${strategy.id}`), {
        isPublic: newValue
      });
      
      setStrategies((prev) =>
        prev.map((s) =>
          s.id === strategy.id ? { ...s, isPublic: newValue } : s
        )
      );
      
      handleCloseMenu();
    } catch (error) {
      console.error('Error updating strategy visibility:', error);
      setError('Failed to update strategy visibility');
    }
  };

  const handleDeployStrategy = (id: string) => {
    navigate(`/strategy/${id}?deploy=true`);
    handleCloseMenu();
  };

  // Format percentage with sign
  const formatPercent = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      signDisplay: 'always',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Live Strategies</Typography>
        <Typography variant="body2" color="text.secondary">
          Strategies currently deployed to exchanges and actively trading
        </Typography>
        {/* No creation functionality on Live Strategies page */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : strategies.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Deployed Strategies Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You don't have any strategies deployed to exchanges yet. Go to the Strategy Lab to create strategies, then deploy them to see them here.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            component="a"
            href="/labs"
          >
            Go to Strategy Lab
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {strategies.map((strategy) => (
            <Grid item xs={12} sm={6} md={4} key={strategy.id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ mr: 2 }}>
                      {strategy.name}
                    </Typography>
                    <Box>
                      <Chip
                        size="small"
                        label={strategy.status}
                        color={
                          strategy.status === 'LIVE'
                            ? 'success'
                            : strategy.status === 'TESTING'
                            ? 'warning'
                            : 'default'
                        }
                      />
                      <IconButton
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={(e) => handleOpenMenu(e, strategy.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '3em' }}>
                    {strategy.description || 'No description provided'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {strategy.lastBacktestResult ? (
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Return
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            strategy.lastBacktestResult.totalReturn >= 0
                              ? 'success.main'
                              : 'error.main'
                          }
                          fontWeight="bold"
                        >
                          {formatPercent(strategy.lastBacktestResult.totalReturn)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Max Drawdown
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          {formatPercent(strategy.lastBacktestResult.maxDrawdown)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Sharpe Ratio
                        </Typography>
                        <Typography variant="body2">
                          {strategy.lastBacktestResult.sharpeRatio.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Symbol
                        </Typography>
                        <Typography variant="body2">
                          {strategy.lastBacktest?.symbol || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No backtest results available
                    </Typography>
                  )}
                </CardContent>

                <Divider />

                <CardActions>
                  <Tooltip title="Edit Strategy">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditStrategy(strategy.id)}
                    >
                      Edit
                    </Button>
                  </Tooltip>

                  <Tooltip title="Run Backtest">
                    <Button
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleEditStrategy(strategy.id)}
                    >
                      Test
                    </Button>
                  </Tooltip>

                  {strategy.status !== 'LIVE' && (
                    <Tooltip title="Deploy to Alpaca">
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<RocketLaunchIcon />}
                        onClick={() => handleDeployStrategy(strategy.id)}
                      >
                        Deploy
                      </Button>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => selectedStrategyId && handleEditStrategy(selectedStrategyId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Strategy</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          const strategy = strategies.find(s => s.id === selectedStrategyId);
          if (strategy) handleTogglePublic(strategy);
        }}>
          <ListItemIcon>
            {strategies.find(s => s.id === selectedStrategyId)?.isPublic 
              ? <VisibilityOffIcon fontSize="small" /> 
              : <VisibilityIcon fontSize="small" />
            }
          </ListItemIcon>
          <ListItemText>
            {strategies.find(s => s.id === selectedStrategyId)?.isPublic 
              ? 'Make Private' 
              : 'Make Public'
            }
          </ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => selectedStrategyId && handleDeployStrategy(selectedStrategyId)}>
          <ListItemIcon>
            <RocketLaunchIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Deploy to Alpaca</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem 
          onClick={() => selectedStrategyId && confirmDelete(selectedStrategyId)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Strategy</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Strategy</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this strategy? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteStrategy} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StrategiesListPage;
